import Alpine from 'alpinejs'

export interface NavigationData {
    mobileMenuOpen: boolean
    activeDesktopSubmenu: string | null
    hideTimeout: number | null
    isTouchDevice: boolean

    toggleMobileMenu(): void
    openMobileMenu(): void
    closeMobileMenu(): void
    trapMobileMenuFocus(event: KeyboardEvent): void
    showDesktopSubmenu(submenuId: string): void
    hideDesktopSubmenu(): void
    hideDesktopSubmenuDelayed(): void
    toggleDesktopSubmenu(submenuId: string): void
    focusFirstSubmenuItem(submenuId: string): void
    focusNextSubmenuItem(event: KeyboardEvent): void
    focusPrevSubmenuItem(event: KeyboardEvent): void
}

export function navigation(): NavigationData {
    return {
        mobileMenuOpen: false,
        activeDesktopSubmenu: null,
        hideTimeout: null,
        isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,

        toggleMobileMenu() {
            if (this.mobileMenuOpen) {
                this.closeMobileMenu()
            } else {
                this.openMobileMenu()
            }
        },

        openMobileMenu() {
            this.mobileMenuOpen = true
            document.body.classList.add('mobile-menu-open')
            // Move focus into the menu once it is shown, so keyboard and
            // screen reader users land on the close button.
            Alpine.nextTick(() =>
                document.getElementById('mobile-menu-close')?.focus(),
            )
        },

        closeMobileMenu() {
            if (!this.mobileMenuOpen) {
                return
            }
            this.mobileMenuOpen = false
            document.body.classList.remove('mobile-menu-open')
            document.getElementById('mobile-menu-toggle')?.focus()
        },

        // Keep Tab and Shift+Tab cycling inside the open mobile menu.
        trapMobileMenuFocus(event: KeyboardEvent) {
            const menu = document.getElementById('mobile-menu')
            if (!menu) {
                return
            }
            const focusable = Array.from(
                menu.querySelectorAll<HTMLElement>('a[href], button'),
            ).filter((element) => element.offsetParent !== null)
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first?.focus()
            }
        },

        showDesktopSubmenu(submenuId: string) {
            if (this.hideTimeout) {
                clearTimeout(this.hideTimeout)
                this.hideTimeout = null
            }
            this.activeDesktopSubmenu = submenuId
        },

        hideDesktopSubmenu() {
            this.activeDesktopSubmenu = null
            if (this.hideTimeout) {
                clearTimeout(this.hideTimeout)
                this.hideTimeout = null
            }
        },

        hideDesktopSubmenuDelayed() {
            this.hideTimeout = window.setTimeout(() => {
                this.activeDesktopSubmenu = null
                this.hideTimeout = null
            }, 150)
        },

        toggleDesktopSubmenu(submenuId: string) {
            this.activeDesktopSubmenu =
                this.activeDesktopSubmenu === submenuId ? null : submenuId
        },

        focusFirstSubmenuItem(submenuId: string) {
            const submenu = document.getElementById(`submenu-${submenuId}`)
            if (submenu) {
                const firstItem = submenu.querySelector(
                    'a[role="menuitem"]',
                ) as HTMLElement
                if (firstItem) {
                    firstItem.focus()
                }
            }
        },

        focusNextSubmenuItem(event: KeyboardEvent) {
            const currentItem = event.target as HTMLElement
            const submenu = currentItem.closest('[role="menu"]')
            if (submenu) {
                const items = Array.from(
                    submenu.querySelectorAll('a[role="menuitem"]'),
                ) as HTMLElement[]
                const currentIndex = items.indexOf(currentItem)
                const nextIndex = (currentIndex + 1) % items.length
                items[nextIndex].focus()
            }
        },

        focusPrevSubmenuItem(event: KeyboardEvent) {
            const currentItem = event.target as HTMLElement
            const submenu = currentItem.closest('[role="menu"]')
            if (submenu) {
                const items = Array.from(
                    submenu.querySelectorAll('a[role="menuitem"]'),
                ) as HTMLElement[]
                const currentIndex = items.indexOf(currentItem)
                const prevIndex =
                    currentIndex <= 0 ? items.length - 1 : currentIndex - 1
                items[prevIndex].focus()
            }
        },
    }
}
